// createPackage — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-package' })` op (DSE2 §3.D8).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI
// (oracle packages variant).
//
// Oracle-only — every other engine throws via the synthesizer's documented
// redirect. The shell editor exposes name + OR REPLACE + EDITIONABLE /
// NONEDITIONABLE flags; bodyText is opaque PL/SQL text the user authors via
// the `[Edit body as SQL]` escape into the procedural Monaco surface.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, PackageShellDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreatePackageArgs {
  readonly package: PackageShellDef;
}

export function buildCreatePackageEdit(args: CreatePackageArgs): DdlEdit {
  return { op: 'create-package', package: args.package };
}

export async function commitCreatePackage(
  ctx: VqeDdlEditContext, args: CreatePackageArgs,
): Promise<void> {
  const edit = buildCreatePackageEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isCreatePackageSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

export function getCreatePackageUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'oracle') return '';
  return 'Packages are an Oracle-only construct. Move shared logic to schemas '
    + 'and routines, or compose multiple CREATE FUNCTION / PROCEDURE calls.';
}

export function validateCreatePackageForEngine(
  engine: SqlEngine, args: CreatePackageArgs,
): string | null {
  if (!isCreatePackageSupported(engine)) {
    return getCreatePackageUnsupportedTooltip(engine);
  }
  const p = args.package;
  if (p.name.name.trim().length === 0) {
    return 'Package name is required.';
  }
  if (p.bodyText.trim().length === 0) {
    return 'Package body text is required (declarations / implementations + END).';
  }
  if (p.oracleEditionable && p.oracleNonEditionable) {
    return 'EDITIONABLE and NONEDITIONABLE are mutually exclusive.';
  }
  return null;
}
